import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';
import { leagueError } from './CompanyLeagues';
import './MemberHome.css';
import './CompanyEvents.css';
import './CompanyLeagues.css';

/* One day of a league: its teams, and its result.

   Two halves, in the order they happen. **The teams** — how many, and how full — because a day
   played in teams needs them to exist before anything else is possible. **The result** — read
   off the day's own scores, or typed.

   ★ Empty teams first, filling second. "Team 1..12" before anybody is in them is the shape an
   admin sets up, and all four ways of filling them (name a captain, import a list, pick by hand,
   shuffle the sign-ups) fill teams that already exist. This screen makes them; each of the four
   lives where it belongs.

   ★ Which event the teams are created ON is not this screen's decision: a season-teams league
   holds them on the league's own event, everything else on the day. The server answers that with
   ``team_event_ids`` and sends back ``team_event_id`` so the screen can say where they went.

   ★ Two ways in, and the screen offers whichever the day actually supports:

     READ IT     the day was scored on the platform, so the finishing order is already known.
                 Nothing is typed; the server ranks the day's own scores and applies the ladder.
                 In a master-scoped league that is each league team's battle points ON THIS DAY —
                 ChallengeInPlanning already records which event a scored challenge stood on, so
                 a team playing the whole season still gets one number per day.

     TYPE IT     the day was a quiz on paper, a walk, a bake-off — the platform has no score for
                 it. Not a fallback: for half the subcompetitions of an office olympics it is the
                 only truthful source.

   ★ Ties are entered by giving two rows the same number. The server splits the slots they
   jointly occupy, so "both first" on a 25,18 ladder is 21.5 each rather than 25 twice — which is
   the only rule that survives a ten-way tie.

   ★ Re-closing replaces the day wholesale, and is the ONLY thing that re-applies a ladder or a
   weight that has changed since. Said on the screen, because a table that moved on its own is
   worse than one that asks for a second press. */

const CompanyLeagueDay = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { leagueId, eventId } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  // 'auto' = let the server read the day's own scores. 'manual' = the ranks below are the truth.
  const [mode, setMode] = useState('auto');
  // The create-empty-teams form. null = closed.
  const [teamDraft, setTeamDraft] = useState(null);
  // { subject_id: rank-as-typed }
  const [ranks, setRanks] = useState({});
  const [notify, setNotify] = useState(true);

  const base = `/company/league/${leagueId}/day/${eventId}`;

  const load = useCallback(() => {
    apiRequest('GET', base)
      .then((res) => {
        setData(res);
        const recorded = res.recorded || [];
        const proposed = res.proposed || [];
        if (recorded.length) {
          // Already scored: show exactly what is in the standing, ready to be corrected.
          setRanks(Object.fromEntries(recorded.map((r) => [r.subject_id, String(r.rank || '')])));
          setMode(recorded.some((r) => r.is_manual) ? 'manual' : 'auto');
        } else {
          setMode(proposed.length ? 'auto' : 'manual');
          setRanks({});
        }
      })
      .catch(() => navigate(`/league/${leagueId}`));
  }, [base, leagueId, navigate]);

  useEffect(() => { load(); }, [load]);

  const call = (path, body) => {
    if (busy) return Promise.resolve();
    setBusy(true);
    setNotice(null);
    return apiRequest('POST', `${base}${path}`, body)
      .catch((err) => { setNotice(leagueError(t, err)); throw err; })
      .finally(() => setBusy(false));
  };

  const nameById = useMemo(() => {
    const map = {};
    ((data && data.competitors) || []).forEach((c) => { map[c.id] = c.name; });
    return map;
  }, [data]);

  /* The proposal, with the places the server would give it. Computed here only so the screen can
     SHOW them before anyone presses anything — the server ranks again on close, by the same rule
     (equal scores share a place, the next place skips: 1, 1, 3). */
  const proposedRows = useMemo(() => {
    const rows = (data && data.proposed) || [];
    let place = 0;
    let previous = null;
    return rows.map((row, index) => {
      if (place === 0 || row.raw_score !== previous) {
        place = index + 1;
        previous = row.raw_score;
      }
      return { ...row, rank: place, name: nameById[row.subject_id] || '?' };
    });
  }, [data, nameById]);

  const closeDay = () => {
    const body = { notify };
    if (mode === 'manual') {
      const ordering = Object.entries(ranks)
        .filter(([, value]) => String(value).trim() !== '')
        .map(([subject_id, value]) => ({ subject_id, rank: Number(value) }))
        .filter((row) => Number.isFinite(row.rank) && row.rank >= 1);
      if (!ordering.length) { setNotice(t('leagueNeedOrder')); return; }
      body.ordering = ordering;
    }
    call('/close', body).then(() => navigate(`/league/${leagueId}`)).catch(() => {});
  };

  if (data === null) {
    return (
      <Base activeNav="home">
        <Card><Back to={`/league/${leagueId}`} /><span className="auth__spinner" /></Card>
      </Base>
    );
  }

  const { day, league, competitors = [], scheme = [], recorded = [], teams = [] } = data;
  const isTeamDay = day.mode === 'teams';

  const makeTeams = () => {
    if (!teamDraft || busy) return;
    call('/teams', {
      count: Number(teamDraft.count) || 0,
      start_number: Number(teamDraft.start_number) || 1,
      prefix: teamDraft.prefix,
    }).then(() => { setTeamDraft(null); load(); }).catch(() => {});
  };

  return (
    <Base activeNav="home">
      <Card>
        <Back to={`/league/${leagueId}`} />
        <Title my="1" className="mt-3">
          <Icon name="flag-checkered" theme="primary" className="me-2" />
          {day.name}
        </Title>

        <p className="bravos-q__hint mb-2">
          {t('leagueLadderIs', { ladder: scheme.join(', ') })}
          {day.weight !== 1 ? ` ${t('leagueWeightIs', { weight: day.weight })}` : ''}
        </p>

        {day.closed_at && (
          <p className="ce-note">
            <Icon name="check" className="me-1" />
            {t('leagueAlreadyScored', { count: recorded.length })}
          </p>
        )}

        {notice && <p className="ce-note ce-note--warn">{notice}</p>}

        {/* ---- THE TEAMS ---- */}
        {isTeamDay && (
          <>
            <Title my="1" className="mt-3">
              <Icon name="users" theme="primary" className="me-2" />
              {t('leagueDayTeams')}
            </Title>
            <p className="bravos-q__hint mb-2">{t('leagueDayTeamsNote')}</p>

            {teams.length === 0 && <p className="ce-note">{t('leagueNoTeamsYet')}</p>}
            {teams.map((team) => (
              <div key={team.team_id} className="ce-person">
                <span className="ce-person__body">
                  <span className="ce-person__name">{team.name}</span>
                  <span className="ce-person__email">
                    {/* An empty team is the normal state here, not a problem: filling comes
                        next, and saying "0 people" plainly is what makes that obvious. */}
                    {t('leagueTeamPlayers', { count: team.players })}
                  </span>
                </span>
              </div>
            ))}

            {!teamDraft ? (
              <button type="button" className="bravos-member__makeadmin mt-2"
                      onClick={() => setTeamDraft({ count: 4, start_number: teams.length + 1,
                                                    prefix: '' })}>
                <Icon name="plus" className="me-1" />{t('leagueMakeTeamsButton')}
              </button>
            ) : (
              <div className="bravos-form mt-2">
                <div className="lg-two">
                  <span>
                    <label>{t('leagueHowManyTeams')}</label>
                    <input type="number" min="1" max="100" value={teamDraft.count}
                           onChange={(e) => setTeamDraft({ ...teamDraft, count: e.target.value })} />
                  </span>
                  <span>
                    <label>{t('leagueStartNumber')}</label>
                    <input type="number" min="1" value={teamDraft.start_number}
                           onChange={(e) => setTeamDraft({ ...teamDraft,
                                                           start_number: e.target.value })} />
                  </span>
                </div>
                <label>{t('leagueTeamPrefix')}</label>
                <input value={teamDraft.prefix} maxLength={40} placeholder={t('leagueTeamPrefixHint')}
                       onChange={(e) => setTeamDraft({ ...teamDraft, prefix: e.target.value })} />
                <p className="ce-check__hint">{t('leagueTeamNamesHint')}</p>
                <div className="ce-actions">
                  <button type="button" className="btn btn-primary" disabled={busy}
                          onClick={makeTeams}>{t('leagueCreateTeams')}</button>
                  <button type="button" className="bravos-member__makeadmin"
                          onClick={() => setTeamDraft(null)}>{t('cancel')}</button>
                </div>
              </div>
            )}
            <hr />
          </>
        )}

        {/* Nobody to rank at all. On a team day that means no teams exist yet; on an individual
            day it means nobody has played. Either way it is fixed above, or on the day. */}
        {competitors.length === 0 && !isTeamDay && (
          <p className="ce-note ce-note--warn">
            {t(`leagueFieldEmpty_${league.subject_type}`)}
          </p>
        )}

        {/* ---- how the result gets in ---- */}
        <Title my="1" className="mt-3">
          <Icon name="flag-checkered" theme="primary" className="me-2" />
          {t('leagueDayResult')}
        </Title>
        <div className="ce-tabs">
          <button
            type="button"
            className={`ce-tab ${mode === 'auto' ? 'is-on' : ''}`}
            disabled={proposedRows.length === 0}
            onClick={() => setMode('auto')}
          >
            {t('leagueReadIt')}
          </button>
          <button
            type="button"
            className={`ce-tab ${mode === 'manual' ? 'is-on' : ''}`}
            onClick={() => setMode('manual')}
          >
            {t('leagueTypeIt')}
          </button>
        </div>

        {mode === 'auto' && (
          proposedRows.length === 0 ? (
            <p className="ce-note">{t('leagueNoAutoResult')}</p>
          ) : (
            <>
              <p className="bravos-q__hint mb-2">{t('leagueReadItNote')}</p>
              <div className="lg-scroll">
                <table className="lg-table">
                  <thead>
                    <tr>
                      <th className="lg-table__rank">#</th>
                      <th>{t(`leagueSubject_${league.subject_type}`)}</th>
                      <th className="lg-table__pts">{t('leagueRawScore')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proposedRows.map((row) => (
                      <tr key={row.subject_id}>
                        <td className="lg-table__rank">{row.rank}</td>
                        <td><span className="lg-name">{row.name}</span></td>
                        <td className="lg-table__pts">{row.raw_score}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )
        )}

        {mode === 'manual' && (
          <>
            <p className="bravos-q__hint mb-2">{t('leagueTypeItNote')}</p>
            {competitors.map((c) => (
              <div key={c.id} className="ce-person lg-rankrow">
                <span className="ce-person__body">
                  <span className="ce-person__name">{c.name}</span>
                </span>
                <span className="ce-person__act">
                  <input
                    type="number"
                    min="1"
                    className="lg-rankinput"
                    value={ranks[c.id] || ''}
                    placeholder="–"
                    onChange={(ev) => setRanks((r) => ({ ...r, [c.id]: ev.target.value }))}
                  />
                </span>
              </div>
            ))}
            {/* Leaving somebody blank is a real answer, and a different one from last place. */}
            <p className="ce-check__hint">{t('leagueBlankIsAbsent')}</p>
          </>
        )}

        <label className="ce-check mt-3">
          <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
          <span>{t('leagueNotifyOnClose')}</span>
        </label>

        <div className="ce-actions">
          <button type="button" className="btn btn-primary" disabled={busy} onClick={closeDay}>
            {day.closed_at ? t('leagueRescore') : t('leagueCloseDay')}
          </button>
          {day.closed_at && (
            <button
              type="button"
              className="bravos-member__makeadmin"
              disabled={busy}
              onClick={() => call('/reopen').then(() => navigate(`/league/${leagueId}`))
                .catch(() => {})}
            >
              {t('leagueReopenDay')}
            </button>
          )}
        </div>
        {day.closed_at && <p className="ce-check__hint">{t('leagueRescoreHint')}</p>}
      </Card>
    </Base>
  );
};

export default CompanyLeagueDay;
